import React, { useState, useEffect, useMemo } from "react";
import PartenaireKataCard from "./PartenaireKataCard";
import { useUser } from "../../contexts/UserContext";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import { getPartenairesKata } from "../../services/partenaireskataServices";
import CreatePartenaireModal from "./CreatePartenaireModal";
import Button from "../../components/Button/Button";
import InfoBubble from "../../components/InfoBubble/InfoBubble";
import { FaPlus, FaFilter } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const GRADES = [
  "1er Dan",
  "2ème Dan",
  "3ème Dan",
  "4ème Dan",
  "5ème Dan",
  "6ème Dan",
  "7ème Dan",
  "8ème Dan",
];

const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const PartenairesKataPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = user ? user.role_id : "Visiteur";

  const [partenaires, setPartenaires] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState("Tous les grades");
  const [onlyWithContact, setOnlyWithContact] = useState(false);

  useEffect(() => {
    const fetchPartenaires = async () => {
      const fetchedPartenaires = await getPartenairesKata();
      setPartenaires(fetchedPartenaires || []);
    };
    fetchPartenaires();
  }, []);

  const refreshPartenairesList = async () => {
    const fetchedPartenaires = await getPartenairesKata();
    setPartenaires(fetchedPartenaires || []);
  };

  const filteredPartenaires = useMemo(() => {
    let list = [...(partenaires || [])];

    const q = normalize(search);
    if (q) {
      list = list.filter((p) => {
        const inClub = normalize(p.club).includes(q);
        const inGrade = normalize(p.passage_grade).includes(q);
        const inContent = normalize(
          (p.content || "").replace(/<[^>]+>/g, " ")
        ).includes(q);
        return inClub || inGrade || inContent;
      });
    }

    if (gradeFilter !== "Tous les grades") {
      list = list.filter((p) => p.passage_grade === gradeFilter);
    }

    if (onlyWithContact) {
      list = list.filter((p) => p.tel || p.mail);
    }

    // tri simple : par grade puis par club
    list.sort((a, b) => {
      const ag = a.passage_grade || "";
      const bg = b.passage_grade || "";
      if (ag === bg) {
        return normalize(a.club).localeCompare(normalize(b.club));
      }
      return ag.localeCompare(bg, "fr", { numeric: true });
    });

    return list;
  }, [partenaires, search, gradeFilter, onlyWithContact]);

  const openCreateModal = () => setCreateModalOpen(true);
  const closeCreateModal = () => setCreateModalOpen(false);

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      {/* Titre */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4">
        <PageHeader
          eyebrow="Utilisateurs & Visiteurs"
          title="Partenaires Kata"
          subtitle="Cette page permet aux judokas de publier ou de rechercher une annonce pour trouver un partenaire en vue d'un passage de grade. Utilisez les filtres pour cibler un grade ou un club en particulier."
          align="center"
          size="lg"
        />
      </div>

      {/* Filtres + actions */}
      <div className="max-w-6xl mx-auto w-full mt-4 mb-3 px-3 sm:px-4">
        <div className="bg-white/90 dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl shadow-sm px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center gap-2 mb-3">
            <FaFilter className="text-primary text-sm" />
            <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-slate-600 dark:text-slate-300">
              Filtres &amp; recherche
            </p>
          </div>

          <div className="flex flex-col md:flex-row md:items-end gap-3">
            {/* Recherche */}
            <div className="flex-1">
              <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1 uppercase tracking-wide">
                Recherche
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Club, grade ou mots-clés…"
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/70 focus:border-primary"
              />
            </div>

            {/* Grade */}
            <div className="w-full md:w-56">
              <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1 uppercase tracking-wide">
                Grade recherché
              </label>
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/70 focus:border-primary"
              >
                <option value="Tous les grades">Tous les grades</option>
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            {/* Contact uniquement */}
            <div className="w-full md:w-52">
              <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1 uppercase tracking-wide">
                Contact direct
              </label>
              <button
                type="button"
                onClick={() => setOnlyWithContact((v) => !v)}
                className={`w-full inline-flex items-center justify-between rounded-xl border px-3 py-2 text-xs sm:text-sm transition-colors ${
                  onlyWithContact
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200"
                }`}
              >
                <span>Avec téléphone ou email</span>
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    onlyWithContact ? "bg-primary" : "bg-slate-300"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Compteur + bouton ajout */}
          <div className="mt-3 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
              {filteredPartenaires.length} partenaire
              {filteredPartenaires.length > 1 ? "s" : ""} trouvé
              {filteredPartenaires.length > 1 ? "s" : ""} sur{" "}
              {partenaires.length}
            </p>

            {(userRole === 1 || userRole === 2 || userRole === 5) && (
              <Button
                text="Ajouter un partenaire"
                onClick={openCreateModal}
                icon={FaPlus}
                className="gap-2 text-xs sm:text-sm"
              />
            )}
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="max-w-6xl mx-auto w-full px-3 sm:px-4 pb-6">
        {filteredPartenaires.length === 0 ? (
          <div className="mt-6 text-center text-sm text-slate-600 dark:text-slate-300">
            Aucun partenaire ne correspond à ces critères pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filteredPartenaires.map((partenaire) => (
              <PartenaireKataCard
                key={partenaire.id}
                partenaire={partenaire}
                userRole={userRole}
                refreshPartenairesList={refreshPartenairesList}
                user={user}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modale création */}
      {createModalOpen && (
        <CreatePartenaireModal
          isOpen={createModalOpen}
          onClose={closeCreateModal}
          refreshList={refreshPartenairesList}
        />
      )}
    </div>
  );
};

export default PartenairesKataPage;
